import * as React from "react";
import { toast } from "@grandvisionhq/common";
import { Types } from "@grandvisionhq/graphql";
import { PaymentServiceProvider, PaymentMethod } from "@grandvisionhq/payments";
import { useSelector, useFeatureToggles, useDispatch } from "@grandvisionhq/state";
import { useSetCustomerDetails } from "../../../../../../../../hooks/operations/hooks/operations/use-set-customer-details.js";
import { useCreateOrder } from "../../../../../../../../hooks/orders/hooks/orders/use-create-order.js";
import { useCreateOrderDraft } from "../../../../../../../../hooks/orders/hooks/orders/use-create-order-draft.js";
import { useRedirectToOrderComplete } from "../../../../../../../../hooks/orders/hooks/orders/use-redirect-to-order-confirm-page.js";
import { getChangedOptIns } from "../../../../../../../../selectors.js";
import { resetExpressCheckout } from "../../../../../../../../StepShipping/StepShipping/actions.js";
import { getShippingDetails } from "../../../../../../../../StepShipping/StepShipping/selectors.js";
import { getSelectedOptIns } from "../../../../../../../../utils/utils/get-selected-opt-ins.js";
import { useCreateApplePaySession } from "../../../../helper-hooks/modules/ExpressCheckout/hooks/apple-pay/helper-hooks/use-create-apple-pay-session.js";
import { useCreatePaymentRequest } from "../../../../helper-hooks/modules/ExpressCheckout/hooks/apple-pay/helper-hooks/use-create-payment-request.js";
import { mapApplePayContactAddress } from "../../../../mappers/modules/ExpressCheckout/hooks/apple-pay/mappers/apple-pay-contact-address.js";
import { getDateOfBirth } from "../../../../mappers/modules/ExpressCheckout/hooks/apple-pay/mappers/apple-pay-date-of-birth.js";
import { useMapShippingAddress } from "../../../../utility-hooks/modules/ExpressCheckout/hooks/apple-pay/utility-hooks/use-map-shipping-address.js";
import { useValidatePaymentData } from "../../../../utility-hooks/modules/ExpressCheckout/hooks/apple-pay/utility-hooks/use-validate-payment-data.js";
import { useValidateShippingData } from "../../../../utility-hooks/modules/ExpressCheckout/hooks/apple-pay/utility-hooks/use-validate-shipping-data.js";
import { ApplePaySessionErrors } from "../../../../../../../../mappers/mappers/errors-mapping.js";
const useApplePay = ({ returnPath, isExpressPayment = true } = {}) => {
  const {
    createApplePaySession,
    isLoading: createApplePaySessionIsLoading,
    updateError: createApplePaySessionError
  } = useCreateApplePaySession();
  const redirectToOrderComplete = useRedirectToOrderComplete();
  const [error, setError] = React.useState();
  const shippingDetails = useSelector(getShippingDetails);
  const changedOptIns = useSelector(getChangedOptIns);
  const { featureConfigs, allowAlternativeShippingState } = useFeatureToggles();
  const mapShippingAddress = useMapShippingAddress();
  const dispatch = useDispatch();
  const validatePaymentData = useValidatePaymentData();
  const validateShippingData = useValidateShippingData();
  const createOrderDraft = useCreateOrderDraft();
  const isExpressCheckout = shippingDetails?.expressCheckout.isEnabled ?? false;
  const [createPaymentRequest, { error: paymentRequestError, loading: paymentRequestLoading }] = useCreatePaymentRequest(isExpressPayment);
  const [createOrder, { error: createOrderError, loading: createOrderLoading }] = useCreateOrder({
    returnPath
  });
  const { setCustomerDetails } = useSetCustomerDetails();
  const isPostalCodeServiceable = React.useCallback(
    (postalCode) => {
      if (allowAlternativeShippingState && featureConfigs.alternativeShippingRegex) {
        if (!postalCode) return false;
        return !featureConfigs.alternativeShippingRegex.test(postalCode);
      }
      return true;
    },
    [featureConfigs.alternativeShippingRegex, allowAlternativeShippingState]
  );
  const payCartWithApplePay = React.useCallback(() => {
    if (typeof ApplePaySession === "undefined" && error !== "UNSUPPORTED") {
      console.error("Apple pay can only be used on Safari.");
      setError(
        "UNSUPPORTED"
        /* UNSUPPORTED */
      );
      toast.failed(ApplePaySessionErrors.ApplePaySessionUnsupported);
      return;
    }
    const paymentRequest = createPaymentRequest();
    if (!paymentRequest) {
      return;
    }
    if (isExpressPayment) {
      if ((paymentRequest.shippingType === "storePickup" || paymentRequest.shippingType === "servicePickup") && !isPostalCodeServiceable(paymentRequest.shippingContact?.postalCode)) {
        console.error("Island addresses are not supported in express");
        setError(
          "UNSUPPORTED"
          /* UNSUPPORTED */
        );
        toast.failed("checkout.address.postalCode.unavailable.errorMessage");
        return;
      }
    }
    const session = new ApplePaySession(12, paymentRequest);
    session.onvalidatemerchant = () => {
      createApplePaySession(session);
    };
    session.onshippingcontactselected = (e) => {
      if (!validateShippingData(e, session, paymentRequest)) {
        return;
      }
      session.completeShippingContactSelection({
        newTotal: paymentRequest.total
      });
      return;
    };
    session.onpaymentauthorized = async (e) => {
      if (isExpressCheckout && !validatePaymentData(e, session)) {
        return;
      }
      const applePayToken = btoa(JSON.stringify(e.payment.token.paymentData));
      try {
        if (isExpressCheckout && e.payment.shippingContact && e.payment.billingContact) {
          const dateOfBirth = getDateOfBirth(shippingDetails?.expressCheckout);
          const phone = e.payment?.shippingContact?.phoneNumber;
          const vatId = shippingDetails?.expressCheckout?.formData?.customerVatId?.value;
          let selectedOptIns = [];
          if (changedOptIns) {
            selectedOptIns = getSelectedOptIns(changedOptIns);
          }
          await setCustomerDetails({
            customerDetails: {
              customerType: Types.CheckoutCustomerType.B2C,
              email: e.payment.shippingContact?.emailAddress,
              ...dateOfBirth ? { dateOfBirth } : {},
              ...phone ? { phone } : {}
            },
            billingAddress: mapApplePayContactAddress(e.payment.billingContact),
            shippingAddress: mapShippingAddress(e.payment),
            ...vatId ? { invoiceDetails: { vatId } } : {},
            marketingEmailsOptIn: selectedOptIns.includes("marketingEmailsOptIn")
          });
        }
        const createOrderDraftPayload = await createOrderDraft({
          paymentDetails: {
            data: { applePayToken },
            isValid: true,
            type: PaymentMethod.APPLE_PAY
          },
          selectedPaymentMethod: {
            type: PaymentMethod.APPLE_PAY,
            provider: PaymentServiceProvider.ADYEN
          },
          returnUrl: "https://not-needed-but-required-by-adyen.com"
        });
        const orderResponse = await createOrder(createOrderDraftPayload);
        session.completePayment(ApplePaySession.STATUS_SUCCESS);
        dispatch(resetExpressCheckout());
        if (orderResponse?.success) {
          redirectToOrderComplete({
            cartId: orderResponse?.cartId || createOrderDraftPayload.checkoutId,
            orderData: orderResponse?.orderDataToken,
            checkoutMethod: "express_checkout_applepay"
          });
        }
      } catch (e2) {
        session.completePayment(ApplePaySession.STATUS_FAILURE);
      }
    };
    session.oncancel = () => {
      try {
        session.abort();
      } catch (e) {
        if (!(e instanceof Error && e.name === "InvalidAccessError")) {
          throw e;
        }
      }
    };
    session.begin();
  }, [
    error,
    createPaymentRequest,
    isExpressPayment,
    isPostalCodeServiceable,
    createApplePaySession,
    validateShippingData,
    isExpressCheckout,
    validatePaymentData,
    createOrderDraft,
    createOrder,
    dispatch,
    shippingDetails?.expressCheckout,
    changedOptIns,
    setCustomerDetails,
    mapShippingAddress,
    redirectToOrderComplete
  ]);
  return [
    payCartWithApplePay,
    {
      error: error || paymentRequestError || createApplePaySessionError || createOrderError,
      loading: paymentRequestLoading || createApplePaySessionIsLoading || createOrderLoading
    }
  ];
};
export {
  useApplePay
};
//# sourceMappingURL=use-apple-pay.js.map
